import React, { memo } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Col,
  FormControl,
  FormLabel,
  FormText,
  Row,
  FormGroup,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import EditableTagsInput from '../../../common/EditableTagsInput';
import { DriveFilePicker } from '../../../common/drive-file-reader';
import MultiCheckboxInput from '../../../common/MultiCheckboxInput';
import CustomCreatableSelect from '../../../common/CreatableSelect';
import CurrencyInput from '../../../common/CurrencyInput';
import StaticInputDisplay from '../../../common/StaticInputDisplay';
import RichTextEditor from '../../../common/RichTextEditor';
import { CheckCircleFill, ExclamationCircleFill, ExclamationTriangleFill, InfoCircleFill } from 'react-bootstrap-icons';
import { getFieldStatusStyles } from '../../../common/fieldStatus';
import { getFormattedDate } from '../../../../helpers/global';

const InputCell = ({
  field,
  formValues,
  onChange,
  className,
  folderId,
  driveFiles,
  driveFilesLoading,
  onUpdateDriveFiles,
  handleRefresh,
  usedFiles = [],
  enableFieldStatus = false,
  fieldTone = null
}) => {
  const resolveFieldValue = source => {
    if (typeof source === 'function') {
      return source(formValues);
    }
    return source;
  };

  const toDateInputValue = value => {
    if (!value) return '';
    if (typeof value === 'string') {
      const datePart = value.slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return getFormattedDate(date);
  };

  const formatCurrencyValue = value => {
    const numericValue = typeof value === 'string' ? Number(value) : value;
    if (numericValue === null || numericValue === undefined || Number.isNaN(numericValue)) {
      return '$0.00';
    }
    return `$${Number(numericValue).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const getStaticToneStyles = tone => {
    const stylesByTone = {
      error: { borderColor: '#dc3545', backgroundColor: '#fff5f5', color: '#842029' },
      warning: { borderColor: '#ffc107', backgroundColor: '#fffaf0', color: '#664d03' },
      success: { borderColor: '#198754', backgroundColor: '#f0fff4', color: '#0f5132' },
      info: { borderColor: '#0dcaf0', backgroundColor: '#f0fcff', color: '#055160' },
      default: { borderColor: '#ced4da', backgroundColor: '#f8f9fa', color: '#495057' }
    };

    return stylesByTone[tone] || stylesByTone.default;
  };

  const getStaticIcon = (iconType, tone) => {
    const iconColorMap = {
      error: '#dc3545',
      warning: '#f59f00',
      success: '#198754',
      info: '#0dcaf0',
      default: '#6c757d'
    };

    const iconColor = iconColorMap[tone] || iconColorMap.default;

    if (iconType === 'error') return <ExclamationCircleFill size={14} color={iconColor} />;
    if (iconType === 'warning') return <ExclamationTriangleFill size={14} color={iconColor} />;
    if (iconType === 'success') return <CheckCircleFill size={14} color={iconColor} />;
    return <InfoCircleFill size={14} color={iconColor} />;
  };

  const activeFieldTone = enableFieldStatus ? fieldTone : null;
  const statusStyles = getFieldStatusStyles(activeFieldTone);

  const wrapWithFieldAnchor = content => {
    if (!enableFieldStatus || !field?.key || !activeFieldTone) return content;
    return <div data-field-key={field.key}>{content}</div>;
  };

  // Consistent label → control spacing (text/currency used my-2; others only mt-2 + mb-0).
  const labelRowClassName = 'mt-2 mb-2 mr-2 d-flex align-items-center';

  const renderLabelRow = ({ showStatusIcon = true, labelProps = {} } = {}) => (
    <div className={labelRowClassName}>
      <FormLabel
        style={{ textWrap: 'nowrap', ...(statusStyles ? statusStyles.label : {}), ...labelProps.style }}
        className={`midFont m-0 ${labelProps.className || ''}`.trim()}
        htmlFor={labelProps.htmlFor}
      >
        {field.label}
      </FormLabel>
      {showStatusIcon && activeFieldTone ? (
        <ExclamationTriangleFill size={14} color={statusStyles.iconColor} className="ml-1" />
      ) : null}
      {field.secondaryLabel && (
        <FormText className="m-0 ml-2 text-muted" style={{ fontSize: 12 }}>
          ({field.secondaryLabel})
        </FormText>
      )}
    </div>
  );

  return wrapWithFieldAnchor(
    <>
      {field?.type === 'checkbox' ? (
        <div className="form-check ml-2 d-flex align-items-center">
          <label className="form-check-label p-1 ml-2 midFont" htmlFor={field.key}>
            {field.label}
          </label>
          <input
            id={field.key}
            type="checkbox"
            checked={formValues[field.key]}
            onChange={e => onChange(e.target.checked)}
            className="form-check-input mt-0"
            style={{ width: 20, height: 20 }}
          />
        </div>
      ) : field?.type === 'radio' ? (
        <>
          {renderLabelRow()}
          <div className="d-flex">
            {field.options.map((option, index) => (
              <div key={index} className="d-flex">
                <FormControl
                  size="sm"
                  type="radio"
                  value={option}
                  onClick={e => {
                    // messages for disabled options
                    if (field.disabledOptions?.(formValues)?.includes(option) && field.disabledToastMessage) {
                      const message = field.disabledToastMessage?.(formValues)?.[option];
                      if (message) {
                        toast.info(message);
                      }
                      return;
                    }

                    // If required is false and the option is already selected, allow deselection
                    if (field.required === false && formValues[field.key] === option) {
                      onChange({ target: { value: null } }); // Clear the selection
                    } else {
                      onChange({ target: { value: option } });
                    }
                  }}
                  checked={formValues[field.key] === option}
                  id={field.key + '_' + option}
                />
                <label
                  htmlFor={field.key + '_' + option}
                  style={{
                    fontSize: 14,
                    cursor: field.disabledOptions?.(formValues)?.includes(option) ? 'not-allowed' : 'pointer'
                  }}
                  className={`ml-2 my-auto ${index === field.options.length - 1 ? 'mr-0' : 'mr-4'} ${
                    field.disabledOptions?.(formValues)?.includes(option) ? 'text-muted' : ''
                  }`}
                >
                  {option}
                </label>
              </div>
            ))}
          </div>
        </>
      ) : field?.type === 'multi-tag-input' ? (
        <EditableTagsInput
          tags={formValues[field.key]}
          onTagsChange={onChange}
          disabled={false}
          type={'text'}
          autoLowerCase={false}
          maxCharactersForTag={20}
        />
      ) : field?.type === 'multi-field' ? (
        <div className="d-flex flex-column">
          <Row className="mb-3">
            {formValues[field.key]?.map((subFieldData, index) => (
              <>
                {field.fields.map(subField => (
                  <Col xs={2} key={subField.key}>
                    <FormLabel className="midFont m-0">{subField.label}</FormLabel>
                    <FormControl
                      size="sm"
                      key={subField.key}
                      className="midFont"
                      type="text"
                      value={subFieldData[subField.key] || ''}
                      onChange={e => {
                        handleMultiFieldChange(field.key, subField.key, e.target.value, index);
                      }}
                    />
                  </Col>
                ))}
                <Col xs={2} className="d-flex align-items-end justify-content-start p-0">
                  <Button variant="outline-danger" onClick={() => handleDeleteSubField(field.key, index)}>
                    X
                  </Button>
                </Col>
              </>
            ))}
          </Row>
          <Button
            variant="outline-dark"
            className="align-self-start align-items-center d-flex justify-content-between"
            size="sm"
            onClick={() => handleAddSubField(additionalField.key)}
          >
            <Plus size={24} />
            {formValues[field.key]?.length === 0 && <span className="mr-2">Click To Add</span>}
          </Button>
        </div>
      ) : field?.type === 'text' ? (
        <div className={className || ''}>
          {renderLabelRow()}
          <FormControl
            as={field.as}
            className="midFont"
            type="text"
            value={formValues[field.key]}
            onChange={e => onChange(e.target.value)}
            size="sm"
            style={statusStyles ? statusStyles.input : undefined}
          />
        </div>
      ) : field?.type === 'rich-text' ? (
        <div className={className || ''}>
          {renderLabelRow()}
          <RichTextEditor
            value={formValues[field.key] || ''}
            onChange={onChange}
            placeholder={field.placeholder || 'Enter notes...'}
            height={field.height || '160px'}
            enableLists={field.enableLists !== false}
            className="estoppel-notes-rich-text"
          />
        </div>
      ) : field?.type === 'static' ? (
        <div className={className || ''}>
          {!field.hideLabel && renderLabelRow({ showStatusIcon: false })}
          {(() => {
            const tone = resolveFieldValue(field.staticTone || field.statusType) || 'default';
            const tooltipText = resolveFieldValue(field.staticTooltip || field.tooltip);
            const rawValue = field.valueFromRow
              ? field.valueFromRow(formValues)
              : formValues[field.key];
            const displayValue =
              field.displayAs === 'currency'
                ? formatCurrencyValue(rawValue)
                : rawValue === null || rawValue === undefined || rawValue === ''
                ? '-'
                : rawValue;

            const derivedIconType = tone === 'error' ? 'error' : tone === 'warning' ? 'warning' : tone;
            const iconType = resolveFieldValue(field.staticIconType) || derivedIconType || 'info';
            const showIcon =
              resolveFieldValue(field.showStaticIcon) !== undefined
                ? Boolean(resolveFieldValue(field.showStaticIcon))
                : Boolean(tooltipText || tone === 'error' || tone === 'warning' || tone === 'success');

            return (
              <div>
                <div
                  className="d-flex align-items-center justify-content-between"
                  style={{
                    minHeight: 30,
                    color: getStaticToneStyles(tone).color
                  }}
                >
                  <span className="midFont mb-0">{displayValue}</span>
                  {showIcon && (
                    <>
                      {tooltipText ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`${field.key}-tooltip`}>{tooltipText}</Tooltip>}
                        >
                          <span className="d-inline-flex align-items-center" style={{ cursor: 'pointer' }}>
                            {getStaticIcon(iconType, tone)}
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <span className="d-inline-flex align-items-center">{getStaticIcon(iconType, tone)}</span>
                      )}
                    </>
                  )}
                </div>
                {(() => {
                  const noteText = resolveFieldValue(field.note);
                  return noteText ? (
                    <FormText className="m-0 text-muted" style={{ fontSize: 12 }}>
                      {noteText}
                    </FormText>
                  ) : null;
                })()}
              </div>
            );
          })()}
        </div>
      ) : field?.type === 'section' ? (
        <>
          <FormLabel className="font-weight-bold m-2">{field.label}</FormLabel>
        </>
      ) : field?.type === 'file' ? (
        <>
          <DriveFilePicker
            label={field.label}
            driveFolder={folderId}
            onFilePickClick={onChange}
            defaultPickedFiles={formValues[field.key]}
            driveFiles={driveFiles}
            driveFilesLoading={driveFilesLoading}
            onUpdateDriveFiles={onUpdateDriveFiles}
            handleRefresh={handleRefresh}
            usedFiles={usedFiles}
          />
        </>
      ) : field.type === 'multi-checkbox' ? (
        <>
          {renderLabelRow({ showStatusIcon: false })}
          <MultiCheckboxInput
            showAllOption={false}
            options={field.options}
            value={formValues[field.key]}
            onChange={onChange}
          />
        </>
      ) : field.type === 'select' ? (
        <FormGroup className="midFont mb-0">
          {renderLabelRow({ labelProps: { htmlFor: field.key } })}
          <div
            style={{
              ...(statusStyles && { outline: `1px solid #FF0000`, borderRadius: '10px' })
            }}
          >
            <CustomCreatableSelect
              id={field.key}
              name={field.key}
              placeholder="Year"
              options={field.options}
              value={formValues[field.key] ? { label: formValues[field.key], value: formValues[field.key] } : ''}
              classNamePrefix="select"
              className="basic-single select-organization border-danger"
              style={{ outline: `1px solid red`, borderRadius: '10px', outlineOffset: '2px' }}
              onChange={e => {
                onChange(e.value);
              }}
              menuPlacement="auto"
              menuLabel={field?.menuLabel}
            />
          </div>
        </FormGroup>
      ) : field.type === 'currency-input' ? (
        <div className={className || ''}>
          {renderLabelRow()}
          <CurrencyInput
            value={
              formValues[field.key] && typeof formValues[field.key] === 'string'
                ? Number(formValues[field.key])
                : formValues[field.key]
            }
            onChange={onChange}
            className="pt-1"
            height="34px"
            inputStyle={statusStyles ? statusStyles.input : undefined}
          />
        </div>
      ) : field.type === 'static-input-display' ? (
        <div className={className || ''}>
          {renderLabelRow()}
          <StaticInputDisplay
            displayAs={field.displayAs || 'text'}
            value={field.valueFromRow ? field.valueFromRow(formValues) : formValues[field.key]}
            inputStyle={statusStyles ? statusStyles.input : undefined}
          />
        </div>
      ) : field?.type === 'date' ? (
        <div className={className || ''}>
          {renderLabelRow()}
          <FormControl
            className="midFont"
            type="date"
            value={toDateInputValue(formValues[field.key])}
            max="9999-12-31"
            onChange={e => {
              const val = e.target.value;
              if (!val) return onChange('');
              const year = val.split('-')[0];
              if (year?.length > 4) return;
              onChange(val);
            }}
            size="sm"
            style={statusStyles ? statusStyles.input : undefined}
          />
        </div>
      ) : null}
    </>
  );
};

export default memo(InputCell);
